// /festive-events — Festive Events (PAGES.md §3.65)
//
// Manage festive/seasonal events. Create events with dates, expected order
// volume, special menus, and production planning parameters.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-001 to FR-FEST-005

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Pagination,
  Modal,
  ConfirmDialog,
  Input,
  TextArea,
  DatePicker,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { Festival, CreateFestivalDto, FestivalStats } from '@/services/festive.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import {
  ROLE_LEVELS,
  EVENT_STATUS_BADGE,
  EVENT_STATUS_FILTER_OPTIONS,
  PAGE_SIZE,
  formatDateRange,
} from './constants';
import { PlusIcon, CalendarIcon, CalendarEmptyIcon, UsersIcon, CurrencyIcon, TrendingUpIcon, EditIcon, TrashIcon, EyeIcon } from './icons';

// ─── Festival Alert Card (spec: FestivalAlertCard — upcoming events) ────────

function FestivalAlertCard({ festival }: { festival: Festival }) {
  const daysUntil = Math.ceil(
    (new Date(festival.start_date).getTime() - Date.now()) / 86400000,
  );
  return (
    <div className={cn(
      GLASS_INTERACTIVE,
      'p-4 animate-slide-up',
      'bg-gradient-to-r from-violet-50/80 to-pink-50/80 dark:from-violet-900/20 dark:to-pink-900/20',
    )}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white flex-shrink-0">
            <CalendarIcon />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{festival.name}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
              {formatDateRange(festival.start_date, festival.end_date)}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold font-mono tabular-nums text-violet-700 dark:text-violet-400">
            {daysUntil > 0 ? `${daysUntil}d` : 'Now'}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {daysUntil > 0 ? 'until start' : 'active'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Event Card ──────────────────────────────────────────────────────

function EventMobileCard({
  event,
  onClick,
  onEdit,
  onDelete,
}: {
  event: Festival;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const currency = useTenantCurrency();
  const badge = EVENT_STATUS_BADGE[event.status] ?? EVENT_STATUS_BADGE.UPCOMING;
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onClick}
          aria-label={`View ${event.name}`}
        >
          <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{event.name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono tabular-nums">
            {formatDateRange(event.start_date, event.end_date)}
          </p>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="p-1.5 rounded-lg text-neutral-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
              aria-label={`Edit ${event.name}`}
            >
              <EditIcon />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 rounded-lg text-neutral-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
              aria-label={`Delete ${event.name}`}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        className="flex items-center justify-between mt-3 w-full text-left"
        onClick={onClick}
        aria-label={`View ${event.name}`}
      >
        <span className="text-sm font-bold font-mono tabular-nums text-violet-700 dark:text-violet-400">
          {event.order_count.toLocaleString('en-IN')} orders
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {formatPaise(event.revenue_forecast, currency)}
        </span>
      </button>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveEventsListContent() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canCreate = roleLevel >= 50; // OWNER+
  const canEdit = roleLevel >= 50; // OWNER+ (backend PATCH /festivals/:id requires OWNER)
  const currency = useTenantCurrency();

  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // ── URL-synced filters ──────────────────────────────────────────────────
  const urlStatus = searchParams.get('status') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [page, setPage] = useState(urlPage);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter) params.status = statusFilter;
    if (page > 1) params.page = String(page);
    setSearchParamsRef.current(params, { replace: true });
  }, [statusFilter, page]);

  // ── Data ────────────────────────────────────────────────────────────────
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  // Tenant-wide KPI aggregate (GitHub issue #293) — real totals across ALL
  // festivals, so Upcoming / Active / Total Forecast stay constant across pages
  // instead of being reduced from the current ≤20-row page slice.
  const [stats, setStats] = useState<FestivalStats>({
    total_count: 0,
    upcoming_count: 0,
    active_count: 0,
    total_forecast: 0,
  });
  // The genuinely soonest upcoming festivals tenant-wide (for the alert strip),
  // not the current page's upcoming rows.
  const [upcomingAlerts, setUpcomingAlerts] = useState<Festival[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── Create / Edit modal ──────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formMaxOrders, setFormMaxOrders] = useState('');
  const [formAdvancePct, setFormAdvancePct] = useState('50');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ── Delete confirmation ──────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Festival | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // The list is page-scoped; the KPI stats and the alert strip are
      // tenant-wide, so they stay constant as you paginate.
      const [result, statsResult, upcoming] = await Promise.all([
        festiveService.listFestivals({ page, status: statusFilter || undefined }),
        festiveService.getFestivalStats(),
        festiveService.listFestivals({ upcoming: true, is_active: true, limit: 3 }),
      ]);
      setFestivals(result.data);
      setTotalItems(result.meta.total);
      setStats(statsResult);
      setUpcomingAlerts(upcoming.data);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load events.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Create / Edit event handlers ──────────────────────────────────────────
  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setFormStartDate('');
    setFormEndDate('');
    setFormMaxOrders('');
    setFormAdvancePct('50');
    setFormError(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((event: Festival) => {
    setEditingId(event.id);
    setFormName(event.name);
    setFormDescription(event.description ?? '');
    // Use the local calendar date so the picker matches the date shown in the list.
    setFormStartDate(new Date(event.start_date).toLocaleDateString('en-CA'));
    setFormEndDate(new Date(event.end_date).toLocaleDateString('en-CA'));
    setFormMaxOrders(event.total_capacity != null ? String(event.total_capacity) : '');
    setFormAdvancePct(String(event.advance_percentage ?? 50));
    setFormError(null);
    setModalOpen(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formName.trim()) { setFormError('Event name is required'); return; }
    if (!formStartDate) { setFormError('Start date is required'); return; }
    if (!formEndDate) { setFormError('End date is required'); return; }
    if (new Date(formEndDate) <= new Date(formStartDate)) {
      setFormError('End date must be after start date');
      return;
    }
    const advancePct = parseInt(formAdvancePct, 10);
    if (formAdvancePct.trim() && (Number.isNaN(advancePct) || advancePct < 0 || advancePct > 100)) {
      setFormError('Advance Payment % must be between 0 and 100');
      return;
    }
    if (formMaxOrders.trim()) {
      const maxOrders = parseInt(formMaxOrders, 10);
      if (Number.isNaN(maxOrders) || maxOrders < 1) {
        setFormError('Max Total Orders must be at least 1');
        return;
      }
    }
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        const updated = await festiveService.updateFestival(editingId, {
          name: formName.trim(),
          description: formDescription.trim() || undefined,
          start_date: formStartDate,
          end_date: formEndDate,
          total_capacity: formMaxOrders ? parseInt(formMaxOrders, 10) : undefined,
          advance_percentage: parseInt(formAdvancePct, 10) || 50,
        });
        toast({ title: `Festive event '${updated.name}' updated`, variant: 'success' });
        setModalOpen(false);
        fetchData();
      } else {
        const dto: CreateFestivalDto = {
          name: formName.trim(),
          description: formDescription.trim() || undefined,
          start_date: formStartDate,
          end_date: formEndDate,
          total_capacity: formMaxOrders ? parseInt(formMaxOrders, 10) : undefined,
          advance_percentage: parseInt(formAdvancePct, 10) || 50,
        };
        const created = await festiveService.createFestival(dto);
        toast({ title: `Festive event '${created.name}' created`, variant: 'success' });
        setModalOpen(false);
        navigate(`/festive-events/${created.id}`);
      }
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : `Failed to ${editingId ? 'update' : 'create'} event`,
      );
    } finally {
      setIsSaving(false);
    }
  }, [editingId, formName, formDescription, formStartDate, formEndDate, formMaxOrders, formAdvancePct, navigate, toast, fetchData]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await festiveService.deleteFestival(deleteTarget.id);
      toast({ title: `"${deleteTarget.name}" deleted`, variant: 'success' });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete event', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchData, toast]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && canCreate) { e.preventDefault(); openCreateModal(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, canCreate, openCreateModal]);

  // ── Page title ──────────────────────────────────────────────────────────
  useEffect(() => { document.title = 'Festive Events — Orio360 Admin'; }, []);

  // KPI figures come from the tenant-wide stats aggregate (issue #293), never
  // from the current page slice.

  // ── Table columns ─────────────────────────────────────────────────────
  const columns: ColumnDef<Festival>[] = useMemo(() => [
    {
      key: 'name', header: 'Event Name',
      render: (_v: unknown, row: Festival) => (
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.name}</span>
      ),
    },
    {
      key: 'start_date', header: 'Date Range',
      render: (_v: unknown, row: Festival) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {formatDateRange(row.start_date, row.end_date)}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: Festival) => {
        const badge = EVENT_STATUS_BADGE[row.status] ?? EVENT_STATUS_BADGE.UPCOMING;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
    {
      key: 'order_count', header: 'Orders', numeric: true,
      render: (_v: unknown, row: Festival) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.order_count.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'revenue_forecast', header: 'Forecast', numeric: true,
      render: (_v: unknown, row: Festival) => (
        <span className="font-mono tabular-nums font-bold text-violet-700 dark:text-violet-400">
          {formatPaise(row.revenue_forecast, currency)}
        </span>
      ),
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: Festival) => (
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/festive-events/${row.id}`)} aria-label={`View ${row.name}`}>
            <EyeIcon />
          </Button>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => openEditModal(row)} aria-label={`Edit ${row.name}`}>
              <EditIcon />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)}
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
              aria-label={`Delete ${row.name}`}>
              <TrashIcon />
            </Button>
          )}
        </div>
      ),
    } as ColumnDef<Festival>,
  ], [currency, canEdit, navigate, openEditModal]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Festive Events"
      subtitle="Manage festive and seasonal events for your restaurant"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festive Events' },
      ]}
      actions={
        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <Button
              variant="primary"
              size="sm"
              onClick={openCreateModal}
              disabled={!canCreate}
              aria-label={canCreate ? 'Create new festive event' : 'OWNER+ role required to create events'}
            >
              <PlusIcon /><span className="ml-1.5">New Event</span>
            </Button>
          </div>
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ Upcoming Festival Alert Cards (spec: FestivalAlertCard) ═══ */}
        {upcomingAlerts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {upcomingAlerts.slice(0, 3).map((f, i) => (
              <div key={f.id} className="animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
                <FestivalAlertCard festival={f} />
              </div>
            ))}
          </div>
        )}

        {/* ═══ KPI Stat Cards ═══ */}
        {!isLoading && festivals.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="animate-slide-up">
              <StatCard title="Total Events" value={String(totalItems)} icon={<CalendarIcon />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
              <StatCard title="Upcoming" value={String(stats.upcoming_count)} icon={<TrendingUpIcon />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
              <StatCard title="Active" value={String(stats.active_count)} icon={<UsersIcon />} />
            </div>
            <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
              <StatCard title="Total Forecast" value={formatPaise(stats.total_forecast, currency)} icon={<CurrencyIcon />} />
            </div>
          </div>
        )}

        {/* ═══ Filters ═══ */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-44">
              <Select
                options={EVENT_STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Statuses"
                clearable
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
            </div>
            <div className={cn(GLASS, 'p-6 space-y-4')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="25%" height={18} />
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="rect" width={70} height={24} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                </div>
              ))}
            </div>
          </div>
        ) : festivals.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<CalendarEmptyIcon />}
            title="No festive events"
            description="Create festive events to manage seasonal orders and special menus"
            action={canCreate ? { label: 'Create Event', onClick: openCreateModal } : undefined}
          />
        ) : (
          <>
            {/* ═══ Desktop DataTable ═══ */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Events ({totalItems})
                </h3>
              </div>
              <DataTable<Festival>
                columns={columns}
                data={festivals}
              />
            </div>

            {/* ═══ Mobile Card List ═══ */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Events ({totalItems})
              </h3>
              {festivals.map((event) => (
                <EventMobileCard
                  key={event.id}
                  event={event}
                  onClick={() => navigate(`/festive-events/${event.id}`)}
                  onEdit={canEdit ? () => openEditModal(event) : undefined}
                  onDelete={canEdit ? () => setDeleteTarget(event) : undefined}
                />
              ))}
            </div>

            {/* ═══ Pagination ═══ */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ Mobile FAB ═══ */}
      {canCreate && (
        <button
          type="button"
          onClick={openCreateModal}
          className="fixed bottom-6 right-6 sm:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-pink-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
          aria-label="New Event"
        >
          <PlusIcon />
        </button>
      )}

      {/* ═══ Create / Edit Event Modal ═══ */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Festive Event' : 'Create Festive Event'}
        variant="wide"
      >
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg" role="alert">
              {formError}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Event / Festival Name *
            </label>
            <Input
              value={formName}
              onChange={(val) => { setFormName(val); setFormError(null); }}
              placeholder="e.g. Diwali 2026, Pongal Special"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                Start Date *
              </label>
              <DatePicker
                value={formStartDate ? new Date(formStartDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setFormStartDate(d.toLocaleDateString('en-CA')); setFormError(null); }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                End Date *
              </label>
              <DatePicker
                value={formEndDate ? new Date(formEndDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setFormEndDate(d.toLocaleDateString('en-CA')); setFormError(null); }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                Max Total Orders
              </label>
              <Input
                type="number"
                min={1}
                value={formMaxOrders}
                onChange={(val) => setFormMaxOrders(val)}
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                Advance Payment %
              </label>
              <Input
                type="number"
                min={0}
                max={100}
                value={formAdvancePct}
                onChange={(val) => setFormAdvancePct(val)}
                placeholder="50"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Description
            </label>
            <TextArea
              value={formDescription}
              onChange={(val) => setFormDescription(val)}
              placeholder="Describe this festive event..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isSaving}>
              {isSaving
                ? editingId ? 'Saving...' : 'Creating...'
                : editingId ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══ Delete Confirmation ═══ */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete "${deleteTarget?.name ?? ''}"?`}
        description="This will remove the festive event. Any associated bookings and orders may be affected."
        confirmLabel="Delete"
        variant="danger"
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageContainer>
  );
}

export function FestiveEventsListPage() {
  return (
    <ErrorBoundary>
      <FestiveEventsListContent />
    </ErrorBoundary>
  );
}
